import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'welcome',
    category: 'getting-started',
    title: 'Willkommen — so funktioniert die Plattform',
    summary: 'Ein kurzer Überblick darüber, was die Plattform leistet und welche Schritte nötig sind, um ein Ergebnis zu erhalten.',
    body: (
      <>
        <p>
          Die EcoEmpower-Plattform hilft Ihnen dabei, die Energie Ihrer Gemeinschaft zu planen und
          zu verwalten — sie prognostiziert den Energiebedarf der Gebäude, optimiert den
          Verbrauchszeitpunkt und schätzt Kosten und Nutzen von Investitionen wie Solaranlagen oder
          Batterien.
        </p>
        <p>Die Arbeit mit der Plattform folgt fünf einfachen Schritten:</p>
        <ol>
          <li><strong>Gemeinschaft anlegen</strong> — vergeben Sie einen Namen, ein Land und eine Währung.</li>
          <li><strong>Gebäude und Anlagen hinzufügen</strong> — die Gebäude Ihrer Gemeinschaft sowie
            alle Geräte wie Solaranlagen, Batterien oder EV-Ladestationen.</li>
          <li><strong>Daten hochladen</strong> — historische Energiedateien (CSV-Dateien), die
            beschreiben, wie viel Energie jedes Gebäude verbraucht, wie viel Ihre Solar-/Windanlage
            erzeugt und wie die Strompreise aussehen.</li>
          <li><strong>Tools ausführen</strong> — Prognose, Optimierung und Langfristige Planung.</li>
          <li><strong>Ergebnisse ansehen</strong> — Diagramme und Kennzahlen, die Sie speichern und
            exportieren können.</li>
        </ol>
        <Tip>
          Die Tools funktionieren am besten mit echten Daten, aber Sie können die Plattform auch
          erkunden, bevor Sie diese haben — die meisten Tools greifen auf illustrative
          Beispielergebnisse zurück, sodass nichts blockiert wird.
        </Tip>
        <ScreenshotPlaceholder
          img="dashboard-overview.png"
          description="The main Dashboard after logging in, showing the community count cards and the three action cards (Communities, Quick Start, Documentation)."
          source="Platform — log in and capture the main Dashboard (the page at the root URL, e.g. http://localhost/)."
          caption="Die Übersicht ist Ihr Ausgangspunkt."
        />
        <h5 className="mt-4">Welche Rolle spielen die Daten?</h5>
        <p>
          Die wichtigste — und häufigste — Aufgabe ist das korrekte Hochladen Ihrer Datendateien.
          Der Abschnitt <strong>Daten vorbereiten &amp; hochladen</strong> in diesem Leitfaden
          erklärt genau, wie das geht, inklusive gebrauchsfertiger Vorlagen. Wird ein Upload
          abgelehnt, erklärt der Abschnitt <strong>Fehlerbehebung</strong> jede Fehlermeldung in
          verständlicher Sprache.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-overview',
    category: 'data',
    title: 'Welche Datendateien benötige ich?',
    summary: 'Die vollständige Liste der Datendateien, wozu jede verwendet wird und wann sie benötigt wird.',
    body: (
      <>
        <p>
          Daten werden als <strong>CSV-Dateien</strong> hochgeladen — einfache Tabellenkalkulationsdateien,
          die Sie in Excel oder Google Sheets erstellen können. Es gibt zwei Gruppen:
        </p>
        <ul>
          <li><strong>Gebäudedaten</strong> — werden für ein bestimmtes Gebäude hochgeladen (Energieverbrauch, Dach-PV, Rechnungen).</li>
          <li><strong>Gemeinschaftsdaten</strong> — werden einmalig für die gesamte Gemeinschaft hochgeladen (gemeinsame Solaranlage, Windturbine, Strompreise, Wetter).</li>
        </ul>

        <h5 className="mt-4">Gebäudedatendateien</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Datei</th><th>Spalten (erforderlich)</th><th>Verwendungszweck</th></tr>
          </thead>
          <tbody>
            <tr><td>Gebäude-Lastprofil</td><td><Code>timestamp</Code>, <Code>consumption_kwh</Code></td><td>Wie viel Strom das Gebäude jede Stunde verbraucht — wird für Prognose und Optimierung benötigt.</td></tr>
            <tr><td>PV-Erzeugung Gebäude</td><td><Code>timestamp</Code>, <Code>generation_kwh</Code></td><td>Wie viel die gebäudeeigene Dach-PV jede Stunde erzeugt hat.</td></tr>
            <tr><td>Gebäude-Energierechnung</td><td><Code>period_start</Code>, <Code>period_end</Code>, <Code>consumption_kwh</Code>, <Code>cost_eur</Code></td><td>Monatliche Rechnungen — werden für die Kostenanalyse verwendet.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Gemeinschaftsdatendateien</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Datei</th><th>Spalten (erforderlich)</th><th>Verwendungszweck</th></tr>
          </thead>
          <tbody>
            <tr><td>PV-Erzeugungsprofil</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Ertrag einer gemeinschaftlich genutzten Solaranlage.</td></tr>
            <tr><td>Windenergie-Erzeugungsprofil</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Ertrag einer Gemeinschaftswindturbine.</td></tr>
            <tr><td>Historischer Bezugstarif</td><td><Code>timestamp</Code>, <Code>import_price_eur_kwh</Code></td><td>Der stündliche Preis, den Sie im Laufe eines Jahres für den Strombezug gezahlt haben.</td></tr>
            <tr><td>Historischer Einspeisungstarif</td><td><Code>timestamp</Code>, <Code>export_price_eur_kwh</Code></td><td>Der Preis, den Sie für die Einspeisung von Strom ins Netz erhalten haben.</td></tr>
            <tr><td>Day-Ahead-Bezugs-/Einspeisungstarif</td><td>wie oben</td><td>Stündliche Preise für den nächsten Tag (24 Zeilen) — täglich für die Day-Ahead-Optimierung hochgeladen.</td></tr>
            <tr><td>Solarwetter</td><td><Code>timestamp</Code>, <Code>ghi_w_m2</Code></td><td>Sonneneinstrahlung und Temperatur — verbessert Solarprognosen.</td></tr>
            <tr><td>Windwetter</td><td><Code>timestamp</Code>, <Code>wind_speed_m_s</Code></td><td>Windgeschwindigkeit — verbessert Windprognosen.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Woher kommen diese Daten?</h5>
        <p>Normalerweise geben Sie diese Daten nicht manuell ein — Sie exportieren sie aus einem bereits genutzten System:</p>
        <ul>
          <li><strong>Gebäudeverbrauch</strong> — aus dem Online-Portal des Smart Meters des Gebäudes,
            von Ihrem Verteilnetzbetreiber (DSO) oder aus dem Kundenkonto Ihres Stromanbieters.</li>
          <li><strong>Solar-/Winderzeugung</strong> — aus der Monitoring-App oder dem Webportal des
            Wechselrichters oder der Turbine (in der Regel vom Installateur eingerichtet).</li>
          <li><strong>Tarife</strong> — aus Ihrem Stromvertrag oder von Ihrem Anbieter; Day-Ahead-Preise
            stammen von der nationalen Strombörse oder ebenfalls von Ihrem Anbieter.</li>
          <li><strong>Wetter</strong> — von einem nationalen Wetterdienst oder einem kostenlosen
            Wetterdatenanbieter. Diese Datei ist optional.</li>
        </ul>
        <p>
          Diese Systeme ermöglichen in der Regel den Export eines gewählten Datumsbereichs als
          CSV- oder Excel-Datei. Sobald Sie diese Datei haben, zeigen Ihnen die nächsten zwei
          Artikel, wie Sie sie in die richtige Form bringen.
        </p>

        <Tip>
          Sie brauchen <strong>nicht</strong> alle Dateien, um anzufangen. Das Wesentliche ist ein
          <strong> Gebäude-Lastprofil</strong> für jedes Gebäude und eine <strong>Tarifdatei</strong>.
          Erzeugung- und Wetterdateien können Sie später hinzufügen, um die Genauigkeit zu verbessern.
        </Tip>
        <p className="mt-3">
          Jeder Dateityp hat einen eigenen kurzen Artikel in diesem Abschnitt, mit einem Beispiel und
          einer herunterladbaren Vorlage. Beginnen Sie mit <em>„CSV-Dateien korrekt erstellen"</em>
          weiter unten — das verhindert die große Mehrheit der Upload-Probleme.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-csv-basics',
    category: 'data',
    title: 'CSV-Dateien korrekt erstellen (Excel &amp; Google Sheets)',
    summary: 'Die goldenen Regeln für eine gültige Datei — und den häufigsten Fehler, den Sie vermeiden sollten.',
    body: (
      <>
        <p>
          Eine <strong>CSV-Datei</strong> ist einfach eine Tabellenkalkulation, die in einem
          einfachen Textformat gespeichert wird. Sie bereiten sie in Excel oder Google Sheets wie
          jede andere Tabelle vor und speichern sie dann als CSV. Befolgen Sie diese Regeln, und
          Ihre Uploads funktionieren beim ersten Mal.
        </p>

        <h5 className="mt-4">Die goldenen Regeln</h5>
        <ol>
          <li><strong>Erste Zeile = Spaltenüberschriften.</strong> Die erste Zeile muss die genauen
            Spaltennamen für den jeweiligen Dateityp enthalten (zum Beispiel <Code>timestamp</Code> und <Code>consumption_kwh</Code>).
            Schreibweise und Kleinbuchstaben müssen exakt übereinstimmen.</li>
          <li><strong>Eine Zeile pro Stunde.</strong> Jede folgende Zeile entspricht einer Stunde
            Daten. Ein volles Jahr hat <strong>8.760 Zeilen</strong> (365 Tage × 24 Stunden) zuzüglich der Überschriftenzeile.</li>
          <li><strong>Verwenden Sie das Zeitstempelformat</strong> <Code>2025-01-01T14:00:00Z</Code> —
            also Jahr-Monat-Tag, der Buchstabe <Code>T</Code>, dann Stunde:Minute:Sekunde, dann der Buchstabe <Code>Z</Code>.</li>
          <li><strong>Zahlen verwenden einen Punkt, niemals ein Komma.</strong> Schreiben Sie <Code>3.5</Code>, nicht <Code>3,5</Code>.</li>
          <li><strong>Keine leeren Zeilen, keine Summen, keine zusätzlichen Notizen</strong> — nur die Überschriftenzeile und Datenzeilen.</li>
        </ol>

        <Warn>
          <strong>Der häufigste Fehler.</strong> In Deutschland, Frankreich, Italien, Tschechien und
          Griechenland schreiben Tabellenkalkulationsprogramme Dezimalzahlen normalerweise mit einem
          <strong> Komma</strong> (<Code>0,457</Code>). Die Plattform erwartet einen <strong>Punkt</strong>{' '}
          (<Code>0.457</Code>), daher wird eine Datei mit Kommas mit „Value must be a number"
          abgelehnt. Wenn Ihre Datei bereits Kommas enthält, müssen Sie sie <strong>nicht</strong>{' '}
          neu eingeben — siehe{' '}
          <ArticleLink to="data-fix-commas">Eine Datei mit Komma-Dezimaltrennzeichen korrigieren</ArticleLink>.
        </Warn>

        <h5 className="mt-4">Zur Zeitstempelspalte</h5>
        <p>
          Das zuverlässigste Format ist <Code>2025-01-01T14:00:00Z</Code>, und es hat einen
          versteckten Vorteil: Excel neigt dazu, alles, was es als Datum erkennt, stillschweigend
          umzuformatieren, lässt diese Form aber in der Regel in Ruhe, weil das <Code>T</Code> und
          das <Code>Z</Code> dabei sind. Die Plattform versteht auch gängige europäische Formate wie{' '}
          <Code>01/01/2025 14:00</Code> und <Code>2025-01-01</Code> — aber genau diese ändert Excel
          gerne im Hintergrund.
        </p>
        <p>
          Wenn Ihre Zeitstempel verändert wurden oder Sie die Spalte neu aufbauen müssen, fügen Sie
          diese Formel in eine freie Spalte ein (hier befindet sich das ursprüngliche Datum/die Zeit
          in Zelle <Code>A2</Code>):
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`=TEXT(A2,"yyyy-mm-ddThh:mm:ss")&"Z"`}
        </pre>
        <p>
          Kopieren Sie die Formel in alle Zeilen, markieren Sie dann die Spalte und verwenden Sie
          <strong> Kopieren → Inhalte einfügen → Werte</strong>, um das Ergebnis zu fixieren. Nutzen
          Sie diese Spalte als Ihren <Code>timestamp</Code>.
        </p>

        <h5 className="mt-4">Wie viele Zeilen? (Datenauflösung)</h5>
        <p>
          Eine Zeile pro Zeitraum. <strong>Stündliche</strong> Daten sind üblich — ein volles Jahr
          hat <strong>8.760 Zeilen</strong> — aber auch <strong>15-Minuten-</strong> und{' '}
          <strong>tägliche</strong> Daten werden akzeptiert. Mindestens 24 Zeilen (ein Tag) sind erforderlich.
        </p>

        <h5 className="mt-4">Als CSV aus Microsoft Excel speichern</h5>
        <ol>
          <li>Klicken Sie auf <strong>Datei → Speichern unter</strong>.</li>
          <li>Wählen Sie im Dateityp-Dropdown <strong>„CSV UTF-8 (durch Trennzeichen getrennt) (*.csv)"</strong>.</li>
          <li>Klicken Sie auf <strong>Speichern</strong>. Wenn Excel warnt, dass einige Funktionen verloren gehen, klicken Sie auf <strong>Ja</strong> — das ist erwartet.</li>
        </ol>
        <p className="small text-muted">
          Wenn Ihre Excel-Version „CSV UTF-8" nicht auflistet, wählen Sie stattdessen „CSV (durch Trennzeichen getrennt)".
        </p>
        <ScreenshotPlaceholder
          img="excel-save-as-csv.png"
          description="Excel's Save As dialog with the file-type dropdown open, highlighting the 'CSV UTF-8 (Comma delimited)' option."
          source="Microsoft Excel — open any workbook → File → Save As → click the file-type dropdown. Capture the dropdown open with the CSV UTF-8 entry highlighted."
          caption={'Wählen Sie in Excel immer „CSV UTF-8".'}
        />

        <h5 className="mt-4">Als CSV aus Google Sheets speichern</h5>
        <ol>
          <li>Klicken Sie auf <strong>Datei → Herunterladen → Kommagetrennte Werte (.csv)</strong>.</li>
          <li>Die Datei wird im Download-Ordner Ihres Computers gespeichert.</li>
        </ol>
        <ScreenshotPlaceholder
          img="gsheets-download-csv.png"
          description="Google Sheets File → Download submenu, highlighting 'Comma-separated values (.csv)'."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Download. Capture the submenu open with 'Comma-separated values (.csv)' highlighted."
          caption="Verwenden Sie in Google Sheets Datei → Herunterladen → .csv."
        />

        <h5 className="mt-4">Datei vor dem Hochladen prüfen</h5>
        <p>
          Öffnen Sie die gespeicherte <Code>.csv</Code>-Datei noch einmal und bestätigen Sie die
          folgenden Punkte. Um ganz sicher zu gehen, öffnen Sie sie mit einem einfachen Texteditor
          (<strong>Notepad</strong> unter Windows, <strong>TextEdit</strong> auf dem Mac) statt mit
          einer Tabellenkalkulation — ein Texteditor zeigt den tatsächlichen Inhalt der Datei,
          während Excel Ihre lokale Formatierung auf dem Bildschirm erneut anwendet und das
          eigentliche Problem verbergen kann.
        </p>
        <p>Bestätigen Sie, dass:</p>
        <ul>
          <li>Die erste Zeile die richtigen Spaltennamen zeigt.</li>
          <li>Zahlen wie <Code>3.5</Code> (Punkt) aussehen, nicht wie <Code>3,5</Code> (Komma).</li>
          <li>Zeitstempel wie <Code>2025-01-01T14:00:00Z</Code> aussehen.</li>
          <li>Keine leeren Zeilen oder Summenzeilen am Ende vorhanden sind.</li>
        </ul>
        <Tip>
          Am einfachsten ist es: Laden Sie eine Vorlage aus einem der dateispezifischen Artikel in
          diesem Abschnitt herunter, ersetzen Sie die Beispielzahlen durch Ihre eigenen und
          speichern Sie. Die Vorlage enthält bereits die richtigen Überschriften und das richtige
          Zahlenformat.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-fix-commas',
    category: 'data',
    title: 'Eine Datei mit Komma-Dezimaltrennzeichen korrigieren',
    summary: 'Konvertieren Sie eine vorhandene Kommazahlen-Datei (3,5) in das von der Plattform benötigte Punktformat (3.5) — ohne etwas neu einzugeben.',
    body: (
      <>
        <p>
          Tabellenkalkulationsprogramme in Deutschland, Frankreich, Italien, Tschechien und
          Griechenland schreiben Dezimalzahlen normalerweise mit einem <strong>Komma</strong> —
          {' '}<Code>0,457</Code> statt <Code>0.457</Code>. Die Plattform benötigt die
          Punktschreibweise. Die gute Nachricht: Sie müssen <strong>nichts</strong> neu eingeben.
          Ändern Sie eine Einstellung, speichern Sie die Datei erneut, und Sie sind fertig. Verwenden
          Sie die Methode, die zu Ihrem Programm passt.
        </p>

        <h5 className="mt-3">Methode 1 — Google Sheets (am einfachsten, kostenlos)</h5>
        <ol>
          <li>Gehen Sie zu <strong>sheets.google.com</strong> und öffnen Sie eine leere Tabelle.</li>
          <li>Wählen Sie <strong>Datei → Importieren → Hochladen</strong> und wählen Sie Ihre CSV-Datei.</li>
          <li>Wählen Sie <strong>Datei → Einstellungen</strong>. Wählen Sie unter <strong>Gebietsschema</strong>
            {' '}<strong>Vereinigte Staaten</strong> (oder Vereinigtes Königreich) und klicken Sie auf
            {' '}<strong>Einstellungen speichern</strong>. Damit weisen Sie Google Sheets an, einen Punkt für Dezimalzahlen zu verwenden.</li>
          <li>Wählen Sie <strong>Datei → Herunterladen → Kommagetrennte Werte (.csv)</strong>.</li>
        </ol>
        <p>Die heruntergeladene Datei verwendet nun Punkte und ist bereit zum Hochladen.</p>
        <ScreenshotPlaceholder
          img="gsheets-locale-setting.png"
          description="Google Sheets — the File → Settings dialog with the Locale dropdown open and 'United States' highlighted."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Settings → the General tab. Capture the modal with the Locale dropdown open and 'United States' highlighted."
          caption={'Durch Setzen des Gebietsschemas auf „Vereinigte Staaten" exportiert Google Sheets Punkte als Dezimaltrennzeichen.'}
        />

        <h5 className="mt-4">Methode 2 — Microsoft Excel</h5>
        <ol>
          <li>Öffnen Sie die Datei in Excel und gehen Sie zu <strong>Datei → Optionen → Erweitert</strong>.</li>
          <li>Deaktivieren Sie unter <strong>Bearbeitungsoptionen</strong> das Kontrollkästchen <strong>„Systemtrennzeichen verwenden"</strong>.</li>
          <li>Setzen Sie das <strong>Dezimaltrennzeichen</strong> auf einen Punkt <Code>.</Code> und
            {' '}das <strong>Tausendertrennzeichen</strong> auf ein einzelnes Leerzeichen (oder lassen Sie es leer).</li>
          <li>Klicken Sie auf <strong>OK</strong>, dann verwenden Sie <strong>Datei → Speichern unter</strong>
            {' '}und wählen Sie <strong>„CSV UTF-8 (durch Trennzeichen getrennt)"</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="excel-separator-options.png"
          description="Excel — File → Options → Advanced, the 'Editing options' area, with 'Use system separators' unticked and the decimal/thousands separator boxes visible."
          source="Microsoft Excel — open any workbook → File → Options → Advanced tab → scroll to 'Editing options'. Capture the section with the 'Use system separators' checkbox unticked."
          caption="Deaktivieren Sie in Excel die Systemtrennzeichen und setzen Sie das Dezimaltrennzeichen auf einen Punkt."
        />
        <Tip>Diese Einstellung wirkt sich auf das gesamte Excel aus. Sie können „Systemtrennzeichen verwenden" danach wieder aktivieren.</Tip>

        <h5 className="mt-4">Methode 3 — Suchen &amp; Ersetzen (letzter Ausweg)</h5>
        <p>
          Verwenden Sie diese Methode nur, wenn die Zahlen als einfacher Text gespeichert sind und
          die Datei <strong>keine</strong> Tausendertrennzeichen enthält (nichts im Stil von{' '}
          <Code>1.234,5</Code>), da ein blindes Ersetzen diese beschädigen würde.
        </p>
        <ol>
          <li>Wählen Sie nur die Spalten aus, die Zahlen enthalten.</li>
          <li>Öffnen Sie <strong>Suchen &amp; Ersetzen</strong> (Strg+H), suchen Sie nach <Code>,</Code>,
            ersetzen Sie durch <Code>.</Code>, dann „Alle ersetzen".</li>
          <li>Prüfen Sie einige Werte vor dem Speichern.</li>
        </ol>

        <h5 className="mt-4">Was ist mit Online-Konverter-Tools?</h5>
        <p>
          Es gibt kostenlose Online-„CSV-Konverter"-Websites, aber diese erfordern das Hochladen
          Ihrer Datei auf eine Drittanbieter-Website, und Energiedaten können sensibel sein. Wir
          empfehlen stattdessen die oben genannten Methoden — sie nutzen Software, die Sie bereits
          haben, und halten die Datei auf Ihrem eigenen Computer. Wenn Ihre Organisation bereits
          Google Workspace nutzt, bleibt Methode 1 im Rahmen vertrauter Tools.
        </p>

        <Warn>
          Eine <Code>.csv</Code>-Datei, die in Excel geöffnet wird, kann <em>aussehen</em>, als
          hätte sie noch Kommas, auch wenn sie mit Punkten gespeichert wurde, weil Excel Ihre lokale
          Formatierung auf dem Bildschirm erneut anwendet. Um zu sehen, was <strong>wirklich</strong>{' '}
          in der Datei steht, öffnen Sie sie mit einem einfachen Texteditor —{' '}
          <strong>Notepad</strong> unter Windows oder <strong>TextEdit</strong> auf dem Mac. Dort
          sollten die Zahlen als <Code>3.5</Code> angezeigt werden.
        </Warn>
      </>
    ),
  },
]
